import { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input } from "reactstrap";
import BreadCrumbs from "../../components/breadCrumbs";
import SideNav from "../../components/sideNav";
import TopNav from "../../components/topNav";
import envConfig from "../../config/constants";
import {
  checkEditAccessRight,
  verifyClassNameAndId,
} from "../../config/utility";
import {
  createSubclass,
  deleteSubclass,
  getSubclasses,
  updateSubclass,
} from "../../redux/api";
import { setMessageUser } from "../../redux/features/user";
import { getAuthKey, getUserData } from "../../redux/selector";

const Subclasses = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isReadOnly = checkEditAccessRight(props.userData.access_right);
  const [subclasses, setSubclasses] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState({ id: null, name: "" });

  const loadSubclasses = useCallback(async () => {
    const response = await dispatch(
      getSubclasses({ authKey: props.authKey, classId: params.class_id }),
    );
    if (response.payload?.status === 200) setSubclasses(response.payload.data);
  }, [dispatch, params.class_id, props.authKey]);

  useEffect(() => {
    if (
      !verifyClassNameAndId(
        props.userData.classes,
        params.class_name,
        params.class_id,
      )
    ) {
      navigate("/app/dashboard");
      return;
    }
    loadSubclasses();
  }, [
    loadSubclasses,
    navigate,
    params.class_id,
    params.class_name,
    props.userData.classes,
  ]);

  const showResult = async (response) => {
    props.setMessageUser({
      message: response.payload.message,
      type:
        response.payload.status === 200
          ? envConfig.notificationTypes.success
          : envConfig.notificationTypes.danger,
    });
    if (response.payload.status === 200) await loadSubclasses();
  };

  return (
    <>
      <TopNav />
      <SideNav classId={params.class_id} className={params.class_name} />
      <div className="listings">
        <BreadCrumbs crumbs={[params.class_name, "Subclasses"]} />
        <div className="listing subclasses-page">
          <div className="listing__header">
            <div className="listing__header__block">
              <h3 className="heading-3 cap">{params.class_name} Subclasses</h3>
            </div>
          </div>

          {!isReadOnly && (
            <Form
              className="subclasses-page__form"
              onSubmit={async (e) => {
                e.preventDefault();
                const response = await dispatch(
                  createSubclass({
                    authKey: props.authKey,
                    classId: params.class_id,
                    body: JSON.stringify({ name }),
                  }),
                );
                if (response.payload.status === 200) setName("");
                await showResult(response);
              }}
            >
              <div className="subclasses-page__add-row">
                <div className="subclasses-page__field">
                  <label className="form__label mb-s-sm c__blue">
                    New subclass
                  </label>
                  <Input
                    className="form__input"
                    value={name}
                    maxLength={100}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Bridges"
                    required
                  />
                </div>
                <div className="subclasses-page__submit">
                  <Button className="btn btn__blue btn__brd" type="submit">
                    + Add Subclass
                  </Button>
                </div>
              </div>
            </Form>
          )}

          {subclasses.map((subclass) => (
            <div className="listing__header mb-sm" key={subclass.id}>
              <div className="listing__header__block">
                {editing.id === subclass.id ? (
                  <Input
                    className="form__input"
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ id: subclass.id, name: e.target.value })
                    }
                  />
                ) : (
                  <span className="cap">
                    {subclass.name}{" "}
                    {Number(subclass.status) === 0 ? "(Disabled)" : ""}
                  </span>
                )}
              </div>
              {!isReadOnly && (
                <div className="listing__header--box">
                  {editing.id === subclass.id ? (
                    <Button
                      className="btn btn__blue btn__brd"
                      onClick={async () => {
                        const response = await dispatch(
                          updateSubclass({
                            authKey: props.authKey,
                            classId: params.class_id,
                            subclassId: subclass.id,
                            body: JSON.stringify({ name: editing.name }),
                          }),
                        );
                        if (response.payload.status === 200)
                          setEditing({ id: null, name: "" });
                        await showResult(response);
                      }}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      className="btn btn__border btn__brd"
                      onClick={() =>
                        setEditing({ id: subclass.id, name: subclass.name })
                      }
                    >
                      Rename
                    </Button>
                  )}
                  <Button
                    className="btn btn__border btn__brd"
                    onClick={async () => {
                      const response =
                        Number(subclass.status) === 1
                          ? await dispatch(
                              deleteSubclass({
                                authKey: props.authKey,
                                classId: params.class_id,
                                subclassId: subclass.id,
                              }),
                            )
                          : await dispatch(
                              updateSubclass({
                                authKey: props.authKey,
                                classId: params.class_id,
                                subclassId: subclass.id,
                                body: JSON.stringify({ status: 1 }),
                              }),
                            );
                      await showResult(response);
                    }}
                  >
                    {Number(subclass.status) === 1 ? "Disable" : "Enable"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default connect(
  (state) => ({
    authKey: getAuthKey(state),
    userData: getUserData(state),
  }),
  { setMessageUser },
)(Subclasses);
